import { useId } from 'react'
import type { AgentBuilderReportData, DashboardWidget } from './types'
import {
  buildGaugeLegend,
  GAUGE_COLORS,
  GAUGE_DIAL,
  GAUGE_PROGRESS_GRADIENT,
  GAUGE_TONES,
  resolveGaugeProgress,
  resolveGaugeValues,
  type GaugeBadge as GaugeBadgeModel,
} from './gauge'
import { formatCompactColumnValue } from './vizUtils'

// Dial geometry in viewBox units (gauge.ts GAUGE_DIAL): the SVG scales to its card, text included.
const VIEW_WIDTH = GAUGE_DIAL.viewWidth
const VIEW_HEIGHT = GAUGE_DIAL.viewHeight
const EDGE_PADDING = GAUGE_DIAL.edgePadding
const CENTER_X = VIEW_WIDTH / 2
const BASELINE_Y = GAUGE_DIAL.baselineY
const BAND_WIDTH = GAUGE_DIAL.bandWidth
const ARC_RADIUS = GAUGE_DIAL.arcRadius
const TICK_INNER_RADIUS = GAUGE_DIAL.tickInnerRadius
const TICK_OUTER_RADIUS = GAUGE_DIAL.tickOuterRadius
const TICK_LABEL_RADIUS = GAUGE_DIAL.tickLabelRadius
// Pixel caps scaled to the wider viewBox (was 360 / 480 for a 384-wide one), so the dial keeps its size.
const MAX_WIDTH = 450
const PRINT_MAX_WIDTH = 600

const COLORS = GAUGE_COLORS
const PROGRESS_GRADIENT = GAUGE_PROGRESS_GRADIENT
const TONES = GAUGE_TONES

/** A point on the dial, `angleDeg` degrees along the arc from its left end (0) to its right end (180). */
function polarToCartesian(cx: number, cy: number, radius: number, angleDeg: number) {
  const angleRad = ((angleDeg - 180) * Math.PI) / 180
  return { x: cx + radius * Math.cos(angleRad), y: cy + radius * Math.sin(angleRad) }
}

function describeArc(cx: number, cy: number, radius: number, startAngle: number, endAngle: number) {
  const start = polarToCartesian(cx, cy, radius, endAngle)
  const end = polarToCartesian(cx, cy, radius, startAngle)
  const largeArc = endAngle - startAngle <= 180 ? '0' : '1'
  return `M ${start.x} ${start.y} A ${radius} ${radius} 0 ${largeArc} 0 ${end.x} ${end.y}`
}

/** The gap-to-target pill: amber while short of the target, green once it is met or beaten. */
export function GaugeBadge({ badge }: { badge: GaugeBadgeModel }) {
  const tone = TONES[badge.tone]
  return (
    <span
      className="whitespace-nowrap rounded-lg px-3 py-1 text-xs font-medium"
      style={{ backgroundColor: tone.badgeBackground, color: tone.badgeText }}
    >
      {badge.text}
    </span>
  )
}

export function GaugeViz({
  data,
  widget,
  printMode = false,
}: {
  data: AgentBuilderReportData
  widget: DashboardWidget
  printMode?: boolean
}) {
  // Called before any early return. useId output contains characters that are not valid in a url(#id).
  const gradientId = `gauge-progress-${useId().replace(/[^a-zA-Z0-9_-]/g, '')}`
  // Not memoized: callers rebuild `data` every render, so a memo would never hit.
  const values = resolveGaugeValues(data, widget)
  const { actual, target, ceiling, actualFormatKey, targetFormatKey, columnFormats } = values

  if (actual == null) {
    return <p className="py-4 text-sm text-gray-400">Gauge requires at least one numeric measure.</p>
  }

  const progress = resolveGaugeProgress(values)
  const legend = buildGaugeLegend(values)
  const headline = formatCompactColumnValue(actual, actualFormatKey, columnFormats)

  // The ceiling sits above both values, so only a negative actual is clipped (to an empty arc).
  const actualPct = Math.max(0, Math.min(actual, ceiling)) / ceiling
  const targetAngle = target != null && target > 0 ? (Math.min(target, ceiling) / ceiling) * 180 : null

  const trackArc = describeArc(CENTER_X, BASELINE_Y, ARC_RADIUS, 0, 180)
  const actualArc = describeArc(CENTER_X, BASELINE_Y, ARC_RADIUS, 0, actualPct * 180)
  const showProgress = actualPct > 0
  const tickInner = targetAngle != null ? polarToCartesian(CENTER_X, BASELINE_Y, TICK_INNER_RADIUS, targetAngle) : null
  const tickOuter = targetAngle != null ? polarToCartesian(CENTER_X, BASELINE_Y, TICK_OUTER_RADIUS, targetAngle) : null
  const tickLabel = targetAngle != null ? polarToCartesian(CENTER_X, BASELINE_Y, TICK_LABEL_RADIUS, targetAngle) : null
  // The label sits outside the dial, on the side the tick leans toward.
  const tickLabelAnchor = targetAngle == null || (targetAngle >= 75 && targetAngle <= 105) ? 'middle' : targetAngle < 75 ? 'end' : 'start'
  const targetLabel = target != null && target > 0 ? `Target ${formatCompactColumnValue(target, targetFormatKey, columnFormats)}` : null
  const percentColor = progress && progress.status !== 'below' ? COLORS.met : COLORS.muted

  const ariaLabel = [`${widget.title} gauge`, headline, progress ? `${progress.percentOfTarget}% of target` : null]
    .filter(Boolean)
    .join(', ')

  return (
    <div className="flex w-full flex-col items-center gap-3">
      <svg
        viewBox={`${-EDGE_PADDING} 0 ${VIEW_WIDTH + EDGE_PADDING * 2} ${VIEW_HEIGHT}`}
        className="w-full"
        style={{ maxWidth: printMode ? PRINT_MAX_WIDTH : MAX_WIDTH }}
        role="img"
        aria-label={ariaLabel}
      >
        {showProgress ? (
          <defs>
            <linearGradient id={gradientId} x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor={PROGRESS_GRADIENT.from} />
              <stop offset="1" stopColor={PROGRESS_GRADIENT.to} />
            </linearGradient>
          </defs>
        ) : null}
        <path d={trackArc} stroke={COLORS.track} strokeWidth={BAND_WIDTH} fill="none" strokeLinecap="round" />
        {showProgress ? (
          <path d={actualArc} stroke={`url(#${gradientId})`} strokeWidth={BAND_WIDTH} fill="none" strokeLinecap="round" />
        ) : null}
        {tickInner && tickOuter && tickLabel && targetLabel ? (
          <>
            <line
              x1={tickInner.x}
              y1={tickInner.y}
              x2={tickOuter.x}
              y2={tickOuter.y}
              stroke={COLORS.tick}
              strokeWidth={2.5}
              strokeLinecap="round"
            />
            <text
              x={tickLabel.x}
              y={tickLabel.y + (tickLabelAnchor === 'middle' ? -4 : 4)}
              textAnchor={tickLabelAnchor}
              fontSize={11}
              fontWeight={500}
              fill={COLORS.targetLabel}
            >
              {targetLabel}
            </text>
          </>
        ) : null}
        <text x={CENTER_X - ARC_RADIUS} y={BASELINE_Y + 26} textAnchor="middle" fontSize={11} fill={COLORS.scale}>
          {formatCompactColumnValue(0, actualFormatKey, columnFormats)}
        </text>
        <text x={CENTER_X + ARC_RADIUS} y={BASELINE_Y + 26} textAnchor="middle" fontSize={11} fill={COLORS.scale}>
          {formatCompactColumnValue(ceiling, actualFormatKey, columnFormats)}
        </text>
        <text
          x={CENTER_X}
          y={BASELINE_Y - 30}
          textAnchor="middle"
          fontSize={34}
          fontWeight={700}
          letterSpacing={-0.5}
          fill={COLORS.headline}
        >
          {headline}
        </text>
        {progress ? (
          <text x={CENTER_X} y={BASELINE_Y - 9} textAnchor="middle" fontSize={12} fontWeight={600} fill={percentColor}>
            {`${progress.percentOfTarget}% of target`}
          </text>
        ) : null}
      </svg>
      <div
        className="grid w-full gap-3 border-t pt-3"
        style={{ borderColor: COLORS.divider, gridTemplateColumns: `repeat(${legend.length}, minmax(0, 1fr))` }}
      >
        {legend.map((entry) => (
          <div key={entry.label} className="min-w-0">
            <div className="text-xs text-gray-500">{entry.label}</div>
            <div className="text-sm font-semibold tabular-nums" style={{ color: entry.tone ? TONES[entry.tone].value : COLORS.headline }}>
              {entry.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
